/**
 * Population RH d'une permission (modèle canonique).
 *
 * Une permission portée sur une ressource de `HR_POPULATION_RESOURCES` dit non seulement *quoi*
 * (le couple ressource × action) mais aussi *sur qui* : les postes ciblés et les types de contrat.
 * Le *où* relève de l'affectation (périmètre boutique) et n'apparaît jamais ici.
 *
 * `null` vaut « tous les postes » et « tous les types de contrat ». Aucune autre forme n'est
 * acceptée : le format v1 (`{ version: 1, anyOf: [...] }`) est refusé, comme toute clé inconnue.
 */

import { collaboratorRole, type CollaboratorRole } from './enums/collaborator-role.js'
import { contractType, type ContractType } from './enums/contract-type.js'
import { isHrPopulationResource, type RbacV2Resource } from './rbac.v2.js'

export interface HrPopulation {
  version: 2
  targetRoles: 'all' | CollaboratorRole[]
  contractTypes: 'all' | ContractType[]
}

export type HrPopulationParseResult =
  | { ok: true; value: HrPopulation | null }
  | { ok: false; errors: string[] }

const COLLABORATOR_ROLES = new Set<string>(collaboratorRole)
const CONTRACT_TYPES = new Set<string>(contractType)

const hasOwn = (value: object, key: PropertyKey): boolean =>
  Reflect.getOwnPropertyDescriptor(value, key) !== undefined

const isPlainJsonObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype

const hasExactKeys = (value: Record<string, unknown>, required: readonly string[]): boolean => {
  const ownKeys = Reflect.ownKeys(value)
  if (ownKeys.some((key) => typeof key !== 'string')) return false
  const keys = ownKeys as string[]
  const allowed = new Set(required)
  return required.every((key) => hasOwn(value, key)) && keys.every((key) => allowed.has(key))
}

const isJsonArray = (value: unknown): value is unknown[] => {
  if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype) return false
  const ownKeys = Reflect.ownKeys(value)
  if (ownKeys.some((key) => typeof key !== 'string')) return false
  const keys = (ownKeys as string[]).filter((key) => key !== 'length')
  return keys.length === value.length && keys.every((key, index) => key === String(index))
}

const parseEnumList = <Value extends string>(
  value: unknown,
  allowed: ReadonlySet<string>,
  path: string,
  errors: string[],
): Value[] | 'all' | null => {
  if (value === 'all') return 'all'
  if (!isJsonArray(value) || value.length === 0) {
    errors.push(`${path}: liste explicite non vide ou "all" requis`)
    return null
  }
  if (value.some((entry) => typeof entry !== 'string' || !allowed.has(entry))) {
    errors.push(`${path}: valeur inconnue`)
    return null
  }
  if (new Set(value as string[]).size !== value.length) {
    errors.push(`${path}: doublon interdit`)
    return null
  }
  return [...value] as Value[]
}

/**
 * Valide et recopie une population issue du JSON. Aucun défaut ni wildcard n'est ajouté : la
 * moindre erreur invalide le grant entier, comme pour les conditions RH v1.
 */
const parseHrPopulationUnsafe = (
  input: unknown,
  resource: RbacV2Resource,
): HrPopulationParseResult => {
  // Une permission sans population (cas de toutes les ressources hors RH) est valide et vaut `null`.
  if (input === null || input === undefined) return { ok: true, value: null }

  if (!isHrPopulationResource(resource)) {
    return { ok: false, errors: [`resource: population non supportée pour ${resource}`] }
  }
  if (!isPlainJsonObject(input)) {
    return { ok: false, errors: ['population: objet JSON attendu'] }
  }

  const errors: string[] = []
  if (!hasExactKeys(input, ['version', 'targetRoles', 'contractTypes'])) {
    errors.push('population: clés attendues version, targetRoles et contractTypes uniquement')
  }
  if (input.version !== 2) errors.push('version: seule la version 2 est acceptée')
  if (errors.length > 0) return { ok: false, errors }

  const targetRoles = parseEnumList<CollaboratorRole>(
    input.targetRoles,
    COLLABORATOR_ROLES,
    'targetRoles',
    errors,
  )
  const contractTypes = parseEnumList<ContractType>(
    input.contractTypes,
    CONTRACT_TYPES,
    'contractTypes',
    errors,
  )

  if (errors.length > 0 || targetRoles === null || contractTypes === null) {
    return { ok: false, errors }
  }
  return { ok: true, value: { version: 2, targetRoles, contractTypes } }
}

export function parseHrPopulation(
  input: unknown,
  resource: RbacV2Resource,
): HrPopulationParseResult {
  try {
    return parseHrPopulationUnsafe(input, resource)
  } catch {
    return { ok: false, errors: ['population: valeur JSON non sûre'] }
  }
}

const canonicalList = (value: 'all' | readonly string[]): string =>
  value === 'all' ? 'all' : [...value].sort().join(',')

/**
 * Clé stable d'une population, listes triées, pour la déduplication des grants côté serveur.
 * `null` et `{ targetRoles: 'all', contractTypes: 'all' }` produisent la même clé : ils ont la
 * même signification.
 */
export const canonicalHrPopulation = (value: HrPopulation | null | undefined): string => {
  if (value === null || value === undefined) return 'v2:roles=all;contracts=all'
  return `v2:roles=${canonicalList(value.targetRoles)};contracts=${canonicalList(
    value.contractTypes,
  )}`
}
